import { useMemo } from "react";
import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { PO_VENDOR_LIST_PER_PAGE } from "../../Components/NewPurchaseOrder/constants";
import {
  mergeNewPurchaseOrderVendorOptions,
} from "../../Components/NewPurchaseOrder/apiUtils";
import {
  buildNewPurchaseOrderVendorsPayload,
  fetchNewPurchaseOrderVendorsList,
} from "../../services/purchaseOrders/purchaseOrdersApi";
import { normalizeVendorListFilters, purchaseOrderKeys } from "./queryKeys";
import { poQueryCache } from "./cacheTimes";

/**
 * Scroll-paginated vendor list (`new_purchase_orders_vendor_list`).
 * When `ensureVendorId` is set, fetches that vendor by id if missing from loaded pages.
 */
export function useNewPurchaseOrderVendorsInfiniteQuery({
  merchantId,
  auth,
  search = "",
  ensureVendorId = "",
  perPage = PO_VENDOR_LIST_PER_PAGE,
  enabled = true,
}) {
  const filters = useMemo(
    () =>
      normalizeVendorListFilters({
        merchantId,
        search,
        perPage,
      }),
    [merchantId, search, perPage]
  );

  const queriesEnabled = Boolean(enabled && merchantId && auth?.token);
  const ensureId =
    ensureVendorId && ensureVendorId !== "all" ? String(ensureVendorId) : "";

  const listQuery = useInfiniteQuery({
    queryKey: purchaseOrderKeys.vendorList(filters),
    queryFn: ({ pageParam, signal }) =>
      fetchNewPurchaseOrderVendorsList(
        buildNewPurchaseOrderVendorsPayload(auth, merchantId, {
          page: pageParam,
          perPage,
          search: filters.search,
        }),
        signal
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.meta.hasMore ? lastPage.meta.page + 1 : undefined,
    enabled: queriesEnabled,
    placeholderData: keepPreviousData,
    ...poQueryCache.vendorList,
  });

  const loadedVendors = useMemo(() => {
    const pages = listQuery.data?.pages;
    if (!Array.isArray(pages)) return [];
    return mergeNewPurchaseOrderVendorOptions(
      pages.flatMap((page) => page?.items ?? [])
    );
  }, [listQuery.data?.pages]);

  const needsEnsureFetch = Boolean(
    ensureId && !loadedVendors.some((v) => String(v.id) === ensureId)
  );

  const ensureQuery = useQuery({
    queryKey: purchaseOrderKeys.vendorById(merchantId, ensureId),
    queryFn: ({ signal }) =>
      fetchNewPurchaseOrderVendorsList(
        buildNewPurchaseOrderVendorsPayload(auth, merchantId, {
          page: 1,
          perPage: 1,
          vendorId: ensureId,
        }),
        signal
      ).then((result) => result.items ?? []),
    enabled: queriesEnabled && needsEnsureFetch,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const vendors = useMemo(() => {
    if (!needsEnsureFetch) return loadedVendors;
    return mergeNewPurchaseOrderVendorOptions(
      loadedVendors,
      ensureQuery.data ?? []
    );
  }, [loadedVendors, needsEnsureFetch, ensureQuery.data]);

  const loadMore = () => {
    if (!listQuery.hasNextPage || listQuery.isFetchingNextPage) return;
    listQuery.fetchNextPage();
  };

  return {
    vendors,
    loadMore,
    hasMore: Boolean(listQuery.hasNextPage),
    isFetching: listQuery.isFetching,
    isFetchingNextPage: listQuery.isFetchingNextPage,
    isPending: listQuery.isPending || (needsEnsureFetch && ensureQuery.isPending),
    error: listQuery.error || ensureQuery.error,
    query: listQuery,
  };
}
